import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShoppingCart, Trash2, Plus, Minus, MessageCircle } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useState } from "react";
import { Link, useLocation } from "wouter";
import { OptimizedImage } from "@/components/OptimizedImage";

export function CartSheet() {
  const { items, totalItems, totalPriceIQD, totalPriceUSD, removeFromCart, updateQuantity, clearCart } = useCart();
  const { t, dir } = useLanguage();
  const [open, setOpen] = useState(false);
  const [, navigate] = useLocation();

  const formatPrice = (price: number, currency: string = "IQD") => {
    if (currency === "USD") {
      return `$${price.toLocaleString()}`;
    }
    return `${price.toLocaleString()} د.ع`;
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative shrink-0 text-white hover:bg-white/20"
          data-testid="button-cart"
        >
          <ShoppingCart className="w-5 h-5" />
          {totalItems > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {totalItems > 99 ? "99+" : totalItems}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-full sm:max-w-md flex flex-col" dir={dir}>
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" />
            {t("cart.title")}
            {totalItems > 0 && (
              <Badge variant="secondary">{totalItems} {t("cart.items")}</Badge>
            )}
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <ShoppingCart className="w-16 h-16 text-muted-foreground mb-4" />
            <p className="text-lg font-medium text-muted-foreground">{t("cart.empty")}</p>
            <p className="text-sm text-muted-foreground mt-2">
              {t("cart.continueShopping")}
            </p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => setOpen(false)}
              asChild
            >
              <Link href="/category/alisveris">{t("cart.continueShopping")}</Link>
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto space-y-3 py-4">
              {items.map((item) => {
                const key = item.isStoreOwnProduct
                  ? `own-${item.storeOwnProductId}`
                  : item.isStoreDropshippingProduct
                    ? `store-ds-${item.storeProductId}`
                    : `listing-${item.listing.id}`;
                return (
                <div 
                  key={key} 
                  className="flex gap-3 p-3 bg-muted/50 rounded-lg"
                >
                  <OptimizedImage
                    src={item.listing.imageUrls?.[0] || "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&q=80&w=100"}
                    alt={item.listing.title}
                    className="w-20 h-20 rounded-md"
                    sizes="80px"
                  />
                  <div className="flex-1 min-w-0">
                    <Link 
                      href={
                        item.isStoreOwnProduct
                          ? `/store-product/${item.storeOwnProductId}?source=own`
                          : item.isStoreDropshippingProduct
                            ? `/store-product/${item.storeProductId}?source=dropshipping`
                            : `/listing/${item.listing.id}`
                      }
                      onClick={() => setOpen(false)}
                      className="font-medium text-sm line-clamp-2 hover:text-primary"
                    >
                      {item.listing.title}
                    </Link>
                    <p className="text-primary font-bold mt-1">
                      {formatPrice(item.listing.price || 0, item.listing.currency || "IQD")}
                    </p>
                    {item.listing.listingType === "WHOLESALE" && item.listing.moq && item.listing.moq > 1 && (
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {t("listing.minOrder")}: {item.listing.moq.toLocaleString("ar-IQ")} {t("listing.piece")}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <Button 
                        variant="outline" 
                        size="icon" 
                        className="h-7 w-7"
                        onClick={() => updateQuantity(
                          item.listing.id,
                          item.quantity - 1,
                          item.isStoreOwnProduct
                            ? { isStoreOwnProduct: true, storeOwnProductId: item.storeOwnProductId }
                            : item.isStoreDropshippingProduct
                              ? { isStoreDropshippingProduct: true, storeProductId: item.storeProductId }
                              : undefined
                        )}
                        disabled={item.listing.listingType === "WHOLESALE" && item.listing.moq ? item.quantity <= item.listing.moq : item.quantity <= 1}
                        data-testid={`button-decrease-${item.listing.id}`}
                      >
                        <Minus className="w-3 h-3" />
                      </Button>
                      <span className="w-8 text-center font-medium">{item.quantity}</span>
                      <Button 
                        variant="outline" 
                        size="icon" 
                        className="h-7 w-7"
                        onClick={() => updateQuantity(
                          item.listing.id,
                          item.quantity + 1,
                          item.isStoreOwnProduct
                            ? { isStoreOwnProduct: true, storeOwnProductId: item.storeOwnProductId }
                            : item.isStoreDropshippingProduct
                              ? { isStoreDropshippingProduct: true, storeProductId: item.storeProductId }
                              : undefined
                        )}
                        disabled={
                          typeof item.listing.quantity === "number" &&
                          item.listing.quantity > 0 &&
                          item.quantity >= item.listing.quantity
                        }
                        data-testid={`button-increase-${item.listing.id}`}
                      >
                        <Plus className="w-3 h-3" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7 text-destructive hover:text-destructive mr-auto"
                        onClick={() => removeFromCart(
                          item.listing.id,
                          item.isStoreOwnProduct
                            ? { isStoreOwnProduct: true, storeOwnProductId: item.storeOwnProductId }
                            : item.isStoreDropshippingProduct
                              ? { isStoreDropshippingProduct: true, storeProductId: item.storeProductId }
                              : undefined
                        )}
                        data-testid={`button-remove-${item.listing.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>

            <SheetFooter className="border-t pt-4 flex-col gap-3">
              <div className="flex justify-between items-center w-full text-lg font-bold">
                <span>{t("cart.total")}:</span>
                <div className="text-primary text-end">
                  {totalPriceIQD > 0 && <div>{formatPrice(totalPriceIQD, "IQD")}</div>}
                  {totalPriceUSD > 0 && <div>{formatPrice(totalPriceUSD, "USD")}</div>}
                </div>
              </div>
              <div className="flex gap-2 w-full">
                <Button 
                  className="flex-1"
                  onClick={() => {
                    setOpen(false);
                    navigate("/checkout");
                  }}
                  data-testid="button-checkout"
                >
                  {t("cart.checkout")}
                </Button>
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={clearCart}
                  data-testid="button-clear-cart"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
